import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import PageHeader from "../PageHeader";
import ApiIcon from "../../../assets/icons/prod_api.svg2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faX } from "@fortawesome/free-solid-svg-icons";
import { proxyAPI } from "../../proxyAPI";
import MyCollapse from "../MyCollapse";
import HelpButton from "../../HelpContent/HelpButton";

export interface ApiProfileType {
  Uuid: string;
  Label: string;
  ClientId: number;
  BaseUrl: string;
  Authenticate: "Basic" | "Bearer";
  AuthHeader: string;
  AuthValue: string;
  Timeout: number;
  Category: string;
  Industry: string;
  DefaultMethod: "GET" | "POST" | "PUT" | "DELETE";
  QueryParameters: {
    Key: string;
    Value: string;
  }[];
  FormData:
    | {
        Key: string;
        Value: string;
      }[]
    | null;
  PostJSON: string;
  JsonPayload: any;
}

export default function ApiConnectionAdvanced({
  api: apiProfile,
  onChange: setApiProfile,
}: {
  api: any;
  onChange: (value: any) => void;
}) {
  const [jsonText, setJsonText] = useState("");
  useEffect(() => {
    if (apiProfile.JsonPayload) {
      setJsonText(JSON.stringify(apiProfile.JsonPayload, null, 2));
    } else {
      setJsonText("");
    }
  }, [apiProfile.JsonPayload]);

  const [jsonError, setJsonError] = useState<string>();

  return (
    <div className="my-4">
      <div className="bg-purple-gradient-darker-reverse rounded-4 p-3 ">
        <div className="mt-5 mb-5">
          <div className="d-flex justify-content-between align-items-start">
            <h5>
              Query Parameters{" "}
              <HelpButton helpKey="api-connection-advanced-query-parameters" />
            </h5>
            <Button
              className="rounded-pill"
              variant="colored"
              onClick={() => {
                setApiProfile({
                  ...apiProfile,
                  QueryParameters: [
                    ...(apiProfile.QueryParameters || []),
                    { Key: "", Value: "" },
                  ],
                });
              }}
            >
              <FontAwesomeIcon icon={faPlus} /> Add
            </Button>
          </div>
          {
            // Query Parameters
            apiProfile.QueryParameters?.map((param: any, index: any) => (
              <div className="row align-items-end" key={index}>
                <div className="col">
                  <label className="form-label">Key</label>
                  <input
                    type="text"
                    className="form-control"
                    value={param.Key}
                    onChange={(e) => {
                      const newParams = [...apiProfile.QueryParameters];
                      newParams[index] = {
                        Key: e.target.value,
                        Value: newParams[index].Value,
                      };
                      setApiProfile({
                        ...apiProfile,
                        QueryParameters: newParams,
                      });
                    }}
                  />
                </div>
                <div className="col">
                  <label className="form-label">Value</label>
                  <input
                    type="text"
                    className="form-control"
                    value={param.Value}
                    onChange={(e) => {
                      const newParams = [...apiProfile.QueryParameters];
                      newParams[index] = {
                        Key: newParams[index].Key,
                        Value: e.target.value,
                      };
                      setApiProfile({
                        ...apiProfile,
                        QueryParameters: newParams,
                      });
                    }}
                  />
                </div>
                <div className="col-auto">
                  <Button
                    variant="danger"
                    className="w-100"
                    onClick={() => {
                      const newParams = [...apiProfile.QueryParameters];
                      newParams.splice(index, 1);
                      setApiProfile({
                        ...apiProfile,
                        QueryParameters: newParams,
                      });
                    }}
                  >
                    <FontAwesomeIcon icon={faX} />
                  </Button>
                </div>
              </div>
            ))
          }
        </div>

        <div className="mb-5">
          <h5>Default content type</h5>
          <select
            className="form-select"
            value={apiProfile.ContentType}
            onChange={(e) =>
              setApiProfile({
                ...apiProfile,
                ContentType: e.target.value as "JSON" | "FORM",
              })
            }
          >
            <option value="JSON">JSON</option>
            <option value="FORM">FORM</option>
          </select>
        </div>

        <div className="mb-5">
          <div className="d-flex justify-content-between align-items-start">
            <h5>
              Post Form Parameters{" "}
              <HelpButton helpKey="api-connection-advanced-post-form-parameters" />
            </h5>
            <Button
              className="rounded-pill"
              variant="colored"
              onClick={() => {
                setApiProfile({
                  ...apiProfile,
                  FormData: [
                    ...(apiProfile.FormData || []),
                    { Key: "", Value: "" },
                  ],
                });
              }}
            >
              <FontAwesomeIcon icon={faPlus} /> Add
            </Button>
          </div>

          {
            // Post Form Parameters
            apiProfile.FormData?.map((param: any, index: any) => (
              <div className="mb-3 row align-items-end" key={index}>
                <div className="col">
                  <label className="form-label">Key</label>
                  <input
                    type="text"
                    className="form-control"
                    value={param.Key}
                    onChange={(e) => {
                      const newParams = [...apiProfile.FormData];
                      newParams[index] = {
                        Key: e.target.value,
                        Value: newParams[index].Value,
                      };
                      setApiProfile({
                        ...apiProfile,
                        FormData: newParams,
                      });
                    }}
                  />
                </div>
                <div className="col">
                  <label className="form-label">Value</label>
                  <input
                    type="text"
                    className="form-control"
                    value={param.Value}
                    onChange={(e) => {
                      const newParams = [...apiProfile.FormData];
                      newParams[index] = {
                        Key: newParams[index].Key,
                        Value: e.target.value,
                      };
                      setApiProfile({
                        ...apiProfile,
                        FormData: newParams,
                      });
                    }}
                  />
                </div>
                <div className="col-auto">
                  <Button
                    variant="danger"
                    className="w-100"
                    onClick={() => {
                      const newParams = [...apiProfile.FormData];
                      newParams.splice(index, 1);
                      setApiProfile({
                        ...apiProfile,
                        FormData: newParams,
                      });
                    }}
                  >
                    <FontAwesomeIcon icon={faX} />
                  </Button>
                </div>
              </div>
            ))
          }
          {["POST", "PUT", "PATCH"].includes(apiProfile.DefaultMethod) &&
            apiProfile.ContentType === "FORM" &&
            !apiProfile.FormData?.length && (
              <div className="alert alert-warning">
                FORM payload is required for {apiProfile.DefaultMethod}{" "}
                requests.
              </div>
            )}
        </div>

        <div className="position-relative mb-5">
          <h5>Post JSON</h5>
          <textarea
            className="form-control"
            rows={10}
            value={jsonText}
            onChange={(e) => {
              setJsonText(e.target.value);
              setJsonError(undefined);
              if (e.target.value === "") {
                setApiProfile({
                  ...apiProfile,
                  JsonPayload: null,
                });
                return;
              }
              try {
                JSON.parse(e.target.value);
                setApiProfile({
                  ...apiProfile,
                  JsonPayload: JSON.parse(e.target.value),
                });
              } catch (err) {
                if (e.target.value !== "") {
                  setJsonError("Invalid JSON");
                }
              }
            }}
          />
          {jsonError && (
            <div className="alert alert-warning my-1">{jsonError}</div>
          )}
          {["POST", "PUT", "PATCH"].includes(apiProfile.DefaultMethod) &&
            apiProfile.ContentType === "JSON" &&
            !apiProfile.JsonPayload && (
              <div className="alert alert-warning">
                JSON payload is required for {apiProfile.DefaultMethod}{" "}
                requests.
              </div>
            )}
        </div>
      </div>
    </div>
  );
}
